import React, {CSSProperties} from 'react';
import {useField} from 'formik';
import classNames from 'classnames';
import {useHistory, useLocation} from 'react-router-dom';
import {useDispatch} from 'react-redux';

import {errorMessage, flashMessage} from 'shared/actions/notifications';
import {deleteCourse} from 'shared/portfolio/actions/courses';
import {CREDIT_OPTIONS, SUBJECT_TYPE_OPTIONS} from 'shared/utils';
import {useMediaQuery} from 'src/hooks/useMediaQuery';

import Label from 'shared/components/label';
import FormikDropdown from 'src/components/formik-dropdown';
import IconTooltip from 'shared/components/icon-tooltip';
import {CourseForm} from './course-form-wrapper';
import CourseTypeDropdown from './course-type-dropdown';
import CourseNameTypeahead from './course-name-typeahead';
import GradeDropdowns from './grade-dropdowns';
import InstitutionTypeahead from './institution-typeahead';

import styles from './inline-course-form.module.scss';

interface Props {
  hideLabels?: boolean;
  prefix: string;
  removeCourse: () => void;
}

const InlineCourseForm = ({hideLabels, prefix, removeCourse}: Props) => {
  const mq = useMediaQuery();
  const history = useHistory();
  const {pathname} = useLocation();
  const dispatch = useDispatch();
  const [field] = useField<CourseForm>(prefix.substring(0, prefix.length - 1));

  const hiddenInstitutionAndSubject = (
    <>
      {(field.value.courseType === 'Dual Enrollment' &&
        field.value.institution.type !== 'CollegeCourseProvider') ||
      (field.value.courseType !== 'Dual Enrollment' &&
        field.value.institution.type === 'CollegeCourseProvider') ? (
        <div className={styles.institution}>
          <InstitutionTypeahead
            name={`${prefix}institution`}
            prefix={prefix}
            typeaheadProps={{size: 'small'}}
          />
        </div>
      ) : null}

      {field.value.name && !field.value.subject && (
        <Label required className={styles.subject}>
          What subject is this course?
          <FormikDropdown
            name={`${prefix}subject`}
            options={SUBJECT_TYPE_OPTIONS}
            required
            size="small"
            testid="bulk-entry-subject"
          />
        </Label>
      )}
    </>
  );

  return (
    <div className={styles.form}>
      <div
        className={styles.course}
        style={{'--grades': field.value.grades.length} as CSSProperties}
      >
        <CourseTypeDropdown
          className={classNames(styles.type, {[styles.hide]: hideLabels})}
          size="small"
          prefix={prefix}
        />

        {mq.SM_AND_DOWN && (
          <Label className={styles.credits} required>
            Credits
            <FormikDropdown
              name={`${prefix}credits`}
              options={CREDIT_OPTIONS}
              size="small"
              required
            />
          </Label>
        )}

        <CourseNameTypeahead
          className={classNames(styles.name, {[styles.hide]: hideLabels})}
          size="small"
          prefix={prefix}
        />

        {mq.SM_AND_DOWN && hiddenInstitutionAndSubject}

        <GradeDropdowns
          className={classNames({[styles.hide]: hideLabels})}
          hideGradeLabel
          size="small"
          prefix={prefix}
        />

        {mq.MD_AND_UP && (
          <div
            className={classNames(styles.cta, {
              [styles.hasLabel]: !hideLabels,
            })}
          >
            {!field.value.new ? (
              <IconTooltip
                iconType="pen"
                iconSize={15}
                onClick={() =>
                  history.replace({
                    pathname: `${pathname}/course/${field.value._id}`,
                    state: {skipScrolling: true},
                  })
                }
                className={styles.edit}
                placement="top"
                testId="bulk-entry-inline-edit"
              >
                Edit Course
              </IconTooltip>
            ) : (
              <div className={styles.spacer}></div>
            )}

            <IconTooltip
              iconType="times-r"
              iconSize={23}
              onClick={async () => {
                removeCourse();

                if (!field.value.new) {
                  try {
                    await dispatch(deleteCourse(field.value._id));
                    dispatch(flashMessage('Deleted course!'));
                  } catch {
                    dispatch(errorMessage());
                  }
                }
              }}
              className={styles.delete}
              placement="top"
              testId="bulk-entry-inline-delete"
            >
              Delete Course
            </IconTooltip>
          </div>
        )}
      </div>

      {mq.MD_AND_UP && hiddenInstitutionAndSubject}
    </div>
  );
};

export default InlineCourseForm;
